/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import {
    KPI_VALUE_DECIMAL_PRECISION
  } from './Constants';
  import { measurementTypes, responseUnitMapping } from './MeasurementTypeInfo';
  import { IunitObject, IscaledData } from './UnitModel';

  /**
   * To convert single value from one unit to other unit
   * If toUnit is provided convert to that else first calculate to which unit to convert and convert accordingly
   */
  export const getConvertedValue = (
    conversionType: string,
    fromUnitName: string,
    value: number | null,
    toUnitSymbol?: string
  ) => {
    if (value === null) {
      return {
        Value: null,
        Unit: fromUnitName
      };
    }
    const convertedValue = getConvertedValueFromUnit(
      conversionType,
      fromUnitName,
      value,
      toUnitSymbol
    );
    return {
      Value: convertedValue.Value,
      Unit: convertedValue.Unit
    };
  };
  
  //This function is added so that to fix "Cannot read properties of null (reading 'toFixed')"
  export const getConvertedNullValue = (
    totalEnergyDeviationPercentage: number | null
  ) => {
    if (totalEnergyDeviationPercentage === null) {
      return null;
    } else {
      return parseFloat(
        totalEnergyDeviationPercentage.toFixed(KPI_VALUE_DECIMAL_PRECISION)
      );
    }
  };
  
  /**
   * Takes array of values, finds highest value and convert that from one unit to other unit
   * If toUnit is provided convert to that else first calculate to which unit to convert and convert accordingly
   */

  
  export const getMeasuremtTypeObj = (conversionType: string) => {
    return measurementTypes.find(type =>
      type.measurementType.includes(conversionType.toLowerCase())
    );
  };
  
  export const getUnitObject = (conversionType: string, unitTypeKey: string) => {
    let unitObject: Array<IunitObject> = [];
    const measurementTypeObj = getMeasuremtTypeObj(conversionType);
    if (measurementTypeObj) {
      unitObject = measurementTypeObj.units[unitTypeKey];
    }
    return unitObject;
  };
  
  export const getFullNameFromSymbol = (
    unitObject: Array<IunitObject>,
    unitSymbol: string
  ) => {
    let fullName: string = '';
    if (unitObject && unitSymbol) {
      const unitObj = unitObject.find(
        unit =>
          unit.symbol.toLowerCase() === unitSymbol.toLowerCase() ||
          unit.displayUnit === unitSymbol
      );
      if (unitObj) {
        fullName = unitObj.name;
      }
    }
    return fullName;
  };
  
  export const getUnitObjectFromValue = (
    unitObject: Array<IunitObject>,
    value: number
  ) => {
    if (unitObject && value >= 0) {
      return unitObject.find(
        unit => unit.minValue <= value && unit.maxValue >= value
      );
    }
  };
  
  export const convertValue = (
    unitObject: Array<IunitObject>,
    value: number,
    fromUnitName: string,
    toUnitName: string
  ) => {
    let convertedValue = 0;
    //Value is provided in smallest unit
    if (fromUnitName == toUnitName) {
      return value;
    }
  
    if (value > 0) {
      let minumumValue = unitObject.find((unit: IunitObject) => {
        return unit.name == toUnitName;
      });
      let toUnitScale = 0;
      if(minumumValue) {
        toUnitScale = minumumValue.minValue;
      }
      if(toUnitScale == 0){
        toUnitScale = 1;
      }
      let fromMinimumValue = unitObject.find((unit: IunitObject) => {
        return unit.name == fromUnitName;
      });
      let fromUnitScale = 0;
      if(fromMinimumValue) {
        fromUnitScale = fromMinimumValue.minValue;
      }
      if(fromUnitScale == 0){
        fromUnitScale = 1;
      }
      if (toUnitScale && fromUnitScale && fromUnitScale > 0) {
        const scaleFactor = toUnitScale / fromUnitScale;
        if (scaleFactor && scaleFactor > 0) {
          convertedValue = value / scaleFactor;
        }
      }
    }
    return convertedValue;
  };
  
  export const getConvertedValueFromUnit = (
    conversionType: string,
    fromUnitName: string,
    value: number,
    toUnitSymbol?: string
  ) => {
    try {
      let absoluteValue = Math.abs(value);
      let finalValue, smallestUnit, unitObj;
  
      const unitTypeKey = responseUnitMapping[fromUnitName.toString()];
      const unitObject = getUnitObject(conversionType, unitTypeKey);
      const emptyUnitObj = {
        name: '',
        symbol: '',
        minValue: 0,
        maxValue: 0,
        displayUnit: ''
      };
  
      if (unitObject) {
        unitObj = unitObject;
        smallestUnit = unitObject[0];
      } else {
        unitObj = [emptyUnitObj];
        smallestUnit = emptyUnitObj;
      }
  
      // convert received value in respective smallest unit
      const valueInSmallestUnit = convertValue(
        unitObject,
        absoluteValue,
        fromUnitName,
        smallestUnit.name
      );
  
      let finalConversionUnitObj = getFinalUnitConversionObject(
        unitObj,
        valueInSmallestUnit,
        toUnitSymbol
      );
  
      if (finalConversionUnitObj) {
        finalValue = convertValue(
          unitObject,
          valueInSmallestUnit,
          smallestUnit.name,
          finalConversionUnitObj.name
        );
  
        return {
          Value: getFinalValue(value, finalValue),
          Unit: finalConversionUnitObj.displayUnit
        };
      }
      return { Value: value, Unit: fromUnitName };
    } catch (exception) {
      return { Value: value, Unit: fromUnitName };
    }
  };
  
  export const getFinalUnitConversionObject = (
    unitObject: Array<IunitObject>,
    valueInSmallestUnit: number,
    toUnitSymbol?: string
  ) => {
    // If toUnit is provided, then convert fromvalue (in smallest unit) to Tounit, else find toUnit by range in which valueInSmallestUnit lies
    if (!toUnitSymbol) {
      return getUnitObjectFromValue(unitObject, valueInSmallestUnit);
    } else {
      const toUnitName = getFullNameFromSymbol(unitObject, toUnitSymbol);
      return unitObject.find(unit => unit.name == toUnitName);
    }
  };
  
  const getFinalValue = (actualValue: number | null, convertedValue: number) => {
    convertedValue = parseFloat(
      convertedValue.toFixed(KPI_VALUE_DECIMAL_PRECISION)
    );
    if (actualValue !== null && actualValue < 0) {
      return -convertedValue;
    }
    return convertedValue;
  };
  export const getConvertedUnit = (
    value: number | null,
    measurementUnit: string | undefined,
    deviationScaledData?: IscaledData
  ) => {
    if (value === 0 && deviationScaledData && deviationScaledData.Value !== 0) {
      return deviationScaledData.Unit;
    } else if (value === null) {
      return ' ';
    } else return measurementUnit;
  };
  export const getAbsoluteValue = (parms: number) => Math.abs(parms);
  export const getConvertedValues = (
    value: number | null,
    deviationScaledData?: IscaledData
  ) => {
    if (value === 0 && deviationScaledData && deviationScaledData.Value !== 0) {
      return getAbsoluteValue(
        JSON.parse(deviationScaledData.Value!.toString()).toFixed(
          KPI_VALUE_DECIMAL_PRECISION
        )
      );
    } else if (value === undefined || value === null) {
      return '--';
    } else
      return getAbsoluteValue(
        JSON.parse(value.toString()).toFixed(KPI_VALUE_DECIMAL_PRECISION)
      );
  };